import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { RuleRow } from '@/types/promoAttribution';
import { formatPromoNumber } from '@/utils/promoFormatNumber';

const RULE_TYPE_CLASS: Record<string, string> = {
  individual: 'bg-sky-100 text-sky-700 border border-sky-200',
  variant: 'bg-violet-100 text-violet-700 border border-violet-200',
  combo: 'bg-orange-100 text-orange-700 border border-orange-200',
};

const STATUS_CLASS: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  inactive: 'bg-zinc-100 text-zinc-600 border border-zinc-200',
};

export function PromoAttributionRulesPage() {
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/spar-mobile/promo-attribution/rules');
        setRules(res.data.data ?? []);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load rules');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = rules.filter((r) => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (filterType !== 'all' && r.rule_type !== filterType) return false;
    if (search && !r.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const ruleTypes = [...new Set(rules.map((r) => r.rule_type))];
  const totalMb = filtered.reduce((acc, r) => acc + (r.total_gb_allocated ?? 0) * 1024, 0);
  const activeCount = filtered.filter((r) => r.status === 'active').length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Promo Data Rules</h1>
        <p className="text-sm text-muted-foreground mt-1">
          All rules with store_scope=all, excluding rules 335 and 855. Includes live attribution stats.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {!loading && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs font-medium text-muted-foreground">Showing Rules</p>
            <p className="text-3xl font-bold mt-1">{formatPromoNumber(filtered.length)}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs font-medium text-muted-foreground">Active</p>
            <p className="text-3xl font-bold text-emerald-600 mt-1">{formatPromoNumber(activeCount)}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs font-medium text-muted-foreground">Data Allocated</p>
            <p className="text-3xl font-bold mt-1">
              {totalMb >= 1024 ? `${formatPromoNumber(totalMb / 1024, 1)} GB` : `${formatPromoNumber(totalMb)} MB`}
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search rule title…"
          className="h-9 w-64 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm focus:outline-none"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm focus:outline-none"
        >
          <option value="all">All types</option>
          {ruleTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <span className="text-xs text-muted-foreground">{filtered.length} results</span>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
          Loading rules…
        </div>
      ) : (
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card border-b">
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2 font-medium">ID</th>
                  <th className="px-4 py-2 font-medium">Title</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Start</th>
                  <th className="px-4 py-2 font-medium">End</th>
                  <th className="px-4 py-2 font-medium text-right">MB</th>
                  <th className="px-4 py-2 font-medium text-right">GTINs</th>
                  <th className="px-4 py-2 font-medium text-right">Triggered</th>
                  <th className="px-4 py-2 font-medium text-right">Attributions</th>
                  <th className="px-4 py-2 font-medium text-right">Data Alloc.</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((rule) => (
                  <tr key={rule.id} className="border-t">
                    <td className="px-4 py-2 font-mono text-sm text-muted-foreground">{rule.id}</td>
                    <td className="px-4 py-2 max-w-xs">
                      <span className="line-clamp-2 text-sm leading-snug">{rule.title}</span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', RULE_TYPE_CLASS[rule.rule_type] ?? '')}>
                        {rule.rule_type}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', STATUS_CLASS[rule.status] ?? '')}>
                        {rule.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{rule.promo_start_date}</td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{rule.promo_end_date}</td>
                    <td className="px-4 py-2 text-right">{formatPromoNumber(rule.mb_reward, 2)}</td>
                    <td className="px-4 py-2 text-right">{formatPromoNumber(rule.gtin_count)}</td>
                    <td className="px-4 py-2 text-right">{formatPromoNumber(rule.gtins_triggered)}</td>
                    <td className="px-4 py-2 text-right">{formatPromoNumber(rule.total_attributions)}</td>
                    <td className="px-4 py-2 text-right text-xs">
                      {rule.total_gb_allocated >= 1
                        ? `${formatPromoNumber(rule.total_gb_allocated, 1)} GB`
                        : `${formatPromoNumber(rule.total_gb_allocated * 1024)} MB`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
